// src/lib/addresses.js
// Session 78n8, Issue #403. The one place that decides what address a document
// prints, for the company and for a location.
//
// WHY THIS IS A SHARED LIB AND NOT A COMPONENT HELPER: a purchase order needs
// it, the requester's confirmation email needs it, the QuickBooks bill (78n9)
// needs it, and the Company profile screen needs it to show a person what a
// vendor will see. Four callers deriving "what is the Ship To" independently is
// how two of them end up disagreeing -- which is the exact defect this file was
// created to prevent, one level up.
//
// THE RULE IT ENCODES, in plain words: D&R's corporate address and its Dallas
// shop are the same building. A location can therefore say "same as the company
// address" instead of repeating it, and then one edit keeps every document
// right. A location that says nothing of the kind uses its own address.
//
// It does NOT collapse the two blocks on the document. A purchase order prints
// the letterhead AND the Ship To in full, always, even when they resolve to the
// same words -- SAP, Coupa and NetSuite all do this, because a shipping clerk
// reads the Ship To block on its own and "same as above" would make the carrier
// hunt. This module decides where the values come from, not how many blocks
// get drawn.

// An address is only usable on a document if it can actually be delivered to.
// Street and city are the floor: a parcel with a street and no city does not
// arrive, and neither does one with a city and no street. State and ZIP are
// checked separately by `addressGaps` so a screen can say what is missing
// rather than only that something is.
export function hasUsableAddress(addr) {
  if (!addr) return false;
  return !!(String(addr.street_address || '').trim() && String(addr.city || '').trim());
}

// What a person still has to fill in, in the words they would use. Returns an
// empty array when the address is complete.
export function addressGaps(addr) {
  const missing = [];
  if (!String(addr?.street_address || '').trim()) missing.push('street address');
  if (!String(addr?.city || '').trim()) missing.push('city');
  if (!String(addr?.state || '').trim()) missing.push('state');
  if (!String(addr?.postal_code || '').trim()) missing.push('ZIP');
  return missing;
}

// The company's own address, as it appears in a document letterhead.
// `legal_name` is what a purchase order must say; `company_name` is the short
// name every screen shows. Falls back to the short name rather than printing
// nothing, because a document with no company name at all is worse than one
// using the informal name.
export function companyAddress(settings) {
  if (!settings) return null;
  return {
    name: settings.legal_name || settings.company_name || null,
    street_address: settings.street_address || null,
    address_line2: settings.address_line2 || null,
    city: settings.city || null,
    state: settings.state || null,
    postal_code: settings.postal_code || null,
    phone: settings.phone || null,
  };
}

// The Ship To for an order raised at `branch`.
//
// `inherited` is returned alongside the values so a caller can say WHY an
// address reads the way it does -- the Company profile screen shows "Same as
// the company address" rather than silently displaying values the person did
// not type into that row, which would look like a bug.
//
// Note the deliberate asymmetry: when a branch inherits, its OWN stored address
// columns are left untouched in the database rather than blanked. Un-ticking
// the box restores whatever was there before, so ticking it is not destructive
// and can be undone by the person who did it.
export function branchShipTo(branch, settings) {
  if (!branch) return null;

  const label = branch.name
    ? ((settings?.company_name || settings?.legal_name || '') + ' — ' + branch.name).replace(/^ — /, '')
    : null;

  if (branch.uses_company_address) {
    const co = companyAddress(settings);
    return {
      name: label,
      street_address: co?.street_address || null,
      address_line2: co?.address_line2 || null,
      city: co?.city || null,
      state: co?.state || null,
      postal_code: co?.postal_code || null,
      // The branch's own phone still wins when it has one: an order shipping to
      // Houston should carry Houston's number for the carrier to ring, even
      // while the street address is inherited.
      phone: branch.phone || co?.phone || null,
      receiving_contact_name: branch.receiving_contact_name || null,
      inherited: true,
    };
  }

  return {
    name: label,
    street_address: branch.street_address || null,
    address_line2: branch.address_line2 || null,
    city: branch.city || null,
    state: branch.state || null,
    postal_code: branch.postal_code || null,
    phone: branch.phone || null,
    receiving_contact_name: branch.receiving_contact_name || null,
    inherited: false,
  };
}

// An address as the lines a document prints, top to bottom, with blanks
// dropped. Returns [] when there is nothing usable, so a caller can test the
// length instead of rendering an empty block with a stray comma in it.
//
// The city/state/ZIP line is assembled here rather than at each call site
// because that is the line whose punctuation is easy to get wrong: "Dallas, TX
// 75201" has a comma after the city and a space -- not a comma -- before the
// ZIP. Getting that wrong on a document sent to a vendor looks amateur in a way
// a missing suite number does not.
export function addressLines(addr, options = {}) {
  if (!addr) return [];
  const lines = [];
  if (options.includeName !== false && addr.name) lines.push(addr.name);
  if (addr.receiving_contact_name) lines.push('Attn: ' + addr.receiving_contact_name);
  if (addr.street_address) lines.push(addr.street_address);
  if (addr.address_line2) lines.push(addr.address_line2);

  const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
  const lastLine = [cityState, addr.postal_code].filter(Boolean).join(' ');
  if (lastLine) lines.push(lastLine);

  if (options.includePhone !== false && addr.phone) lines.push(addr.phone);
  return lines;
}

// The same thing on one line, for a list row rather than a document.
export function addressOneLine(addr) {
  return addressLines(addr, { includeName: false, includePhone: false }).join(' · ');
}
